import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { AnimatePresence, MotionConfig, motion } from "framer-motion"
import { RouterProvider } from "react-router-dom"
import { SessionProvider, useSession } from "@/entities/session"
import { LoginPage } from "@/pages/login"
import { TRANSITION } from "@/shared/lib/motion"
import { usePointerEventsGuard } from "@/shared/hooks/use-pointer-events-guard"
import { ConfirmProvider } from "@/shared/ui/confirm-provider"
import { Toaster } from "@/shared/ui/sonner"
import { TooltipProvider } from "@/shared/ui/tooltip"
import { router } from "./routes/router"

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5,
			refetchOnWindowFocus: false,
		},
	},
})

/** Пока сессия не восстановлена, заглушка, без сессии: вход, дальше роутер. */
function AppContent() {
	const { isRestoring, isAuthenticated } = useSession()

	if (isRestoring) {
		return (
			<div className="grid min-h-dvh place-items-center bg-background">
				{/* Обычно сессия восстанавливается за мгновение: тогда виден только фон, без мелькания. */}
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ ...TRANSITION.crossfade, delay: 0.6 }}
					className="space-y-4 text-center"
				>
					<div className="mx-auto size-12 animate-spin rounded-full border-b-2 border-primary" />
					<p className="text-sm text-muted-foreground">Проверяем доступ…</p>
				</motion.div>
			</div>
		)
	}

	// Вход и выход меняют экран перекрёстным затуханием, а не мгновенной подменой. Только
	// opacity: transform на предке сломал бы `position: fixed` у боковой колонки.
	return (
		<AnimatePresence initial={false} mode="wait">
			<motion.div
				key={isAuthenticated ? "app" : "login"}
				initial={{ opacity: 0 }}
				animate={{ opacity: 1, transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] } }}
				exit={{ opacity: 0, transition: { duration: 0.2, ease: [0.4, 0, 1, 1] } }}
			>
				{isAuthenticated ? <RouterProvider router={router} /> : <LoginPage />}
			</motion.div>
		</AnimatePresence>
	)
}

/** Корневой компонент: React Query, тосты, подтверждения и сессия. */
function App() {
	usePointerEventsGuard()
	return (
		<QueryClientProvider client={queryClient}>
			<MotionConfig reducedMotion="user">
				<TooltipProvider delayDuration={200}>
					<ConfirmProvider>
						<SessionProvider>
							<AppContent />
							<Toaster position="bottom-center" />
						</SessionProvider>
					</ConfirmProvider>
				</TooltipProvider>
			</MotionConfig>
		</QueryClientProvider>
	)
}

export default App
